import { FC } from 'react';
import { withStyles, Card, Icon, InteracWordmark, MaterialSymbol, PaymentMethodType } from 'vopay';
import { Title, CircleButton } from 'UI';
import styles from './Options.module.scss';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { useTranslation } from 'react-i18next';

const cn = withStyles(styles);

export type OptionPaymentMethod = Extract<
  PaymentMethodType,
  'BankAccount' | 'CreditCard' | 'DebitCard' | 'Email' | 'PayPal' | 'Venmo' | 'Zelle'
>;

type OptionsProps = {
  omit?: OptionPaymentMethod[];
  onClose: () => void;
  onSelect: (option: OptionPaymentMethod) => void;
};

const Options: FC<OptionsProps> = ({ onClose, omit = [], onSelect }) => {
  const { LockedSelectOptions } = useLockedFeatures();
  const {
    response: { Country },
  } = useClientData();
  const { t } = useTranslation('translations', {
    keyPrefix: 'Customers.PaymentMethodsTab.ProvidePaymentMethod.Options',
  });

  const isUS = Country === 'US';

  return (
    <Card hoverEffect={false} className={cn('optionsContainer')}>
      <CircleButton
        icon="close"
        style={{ display: 'block', marginLeft: 'auto' }}
        onClick={onClose}
        circleFill={false}
        size="large"
      />

      <Title level="4">{t('title', { defaultValue: 'Select Payment Method' })}</Title>

      <div className={cn('cards')}>
        {!omit.includes('BankAccount') && (
          <CardLink lockedFeature="Bank Account" onClick={() => onSelect('BankAccount')}>
            <Icon icon="bank2" size="lg" color="primary-dark-transparent" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Bank Account'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}
              {t('bankAccount', { defaultValue: 'Bank Account' })}
            </span>
          </CardLink>
        )}

        {!omit.includes('CreditCard') && (
          <CardLink lockedFeature="Credit Card" onClick={() => onSelect('CreditCard')}>
            <Icon icon="creditCard2" size="lg" color="primary-dark-transparent" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Credit Card'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}
              {t('creditCard', { defaultValue: 'Credit Card' })}
            </span>
          </CardLink>
        )}

        {!omit.includes('DebitCard') && (
          <CardLink lockedFeature="Debit Card" onClick={() => onSelect('DebitCard')}>
            <Icon icon="creditCard2" size="lg" color="primary-dark-transparent" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Debit Card'] && (
                <MaterialSymbol icon="lock" fillAmount={1} />
              )}{' '}
              {t('debitCard', { defaultValue: 'Debit Card' })}
            </span>
          </CardLink>
        )}

        {/* Interac e-Transfer is Canada-only. */}
        {!omit.includes('Email') && !isUS && (
          <CardLink lockedFeature="Email" onClick={() => onSelect('Email')}>
            <Icon icon="interacColor" size="lg" color="none" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Email'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              <InteracWordmark />
            </span>
          </CardLink>
        )}

        {!omit.includes('PayPal') && (
          <CardLink lockedFeature="PayPal" onClick={() => onSelect('PayPal')}>
            <Icon icon="paypal" size="lg" color="none" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['PayPal'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('paypal', { defaultValue: 'PayPal' })}
            </span>
          </CardLink>
        )}

        {isUS && !omit.includes('Venmo') && (
          <CardLink lockedFeature="Venmo" onClick={() => onSelect('Venmo')}>
            <Icon icon="venmo" size="lg" color="none" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Venmo'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('venmo', { defaultValue: 'Venmo' })}
            </span>
          </CardLink>
        )}

        {isUS && !omit.includes('Zelle') && (
          <CardLink lockedFeature="Zelle" onClick={() => onSelect('Zelle')}>
            <Icon icon="zelle" size="lg" color="none" />
            <span className={cn('label')}>
              {LockedSelectOptions && LockedSelectOptions['Zelle'] && <MaterialSymbol icon="lock" fillAmount={1} />}
              {t('zelle', { defaultValue: 'Zelle' })}
            </span>
          </CardLink>
        )}
      </div>
    </Card>
  );
};

export default Options;

function CardLink({
  children,
  onClick,
  lockedFeature,
}: {
  children: React.ReactNode;
  onClick: () => void;
  lockedFeature: string;
}) {
  const { LockedSelectOptions } = useLockedFeatures();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const isLocked = LockedSelectOptions && LockedSelectOptions[lockedFeature];

  return (
    <button
      onClick={() => {
        if (isLocked) {
          updateIsLocked(true, LockedSelectOptions[lockedFeature]);
        } else {
          onClick();
        }
      }}
    >
      <Card
        style={{
          borderWidth: 1,
          borderColor: '#2d2d2d07',
        }}
        className={cn('card')}
      >
        <div className={cn('cardContent', isLocked && 'locked')}>{children}</div>
      </Card>
    </button>
  );
}
